import React, { useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import {
  Card,
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Grid,
} from "@mui/material";
import {
  VolunteerActivismOutlined as DonationIcon,
  ShoppingCartOutlined as SaleIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  CalendarTodayOutlined as CalendarIcon,
} from "@mui/icons-material";
import {
  Share2,
  ArrowRight,
} from "lucide-react";
import moment from "moment";
import web3 from "../../../../Service/web3";
import getImageUrlByHash from "../../../../../static/logoUtils";
import AutobetLogo from "../../../../../static/Images/logo.png";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { DEFAULT_CURRENCY, DEFAULT_TOKEN_SYMBOL } from "../../../../../static/Constants";
import { CommonButton, CommonChip } from "../../../../Components/ui";

const safeFromWei = (val, unit = "ether") => {
  if (!val || val === "0" || val === 0) return "0";
  try {
    return web3.utils.fromWei(`${val}`, unit);
  } catch (err) {
    try {
      const num = Number(val);
      if (isNaN(num)) return "0";
      return (num / 1e18).toFixed(4);
    } catch {
      return "0";
    }
  }
};

const shortenAddress = (addr) => {
  if (!addr) return "";
  if (addr.length <= 10) return addr;
  return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
};

const PublicGiftCard = ({
  item,
  giftcardLogo = [],
  viewMode = "grid",
  onShare,
}) => {
  const history = useHistory();
  const [copied, setCopied] = useState(false);

  if (!item) return null;

  const cardId = item.giftCardId !== undefined && item.giftCardId !== null ? item.giftCardId : (item.id ?? 0);
  const isDonation = item.isDonation === true || item.isDonation === "true" || item.isDonation === 1;
  const tokenValue = safeFromWei(item.BETValue, "ether");
  const tokenName = item.tokenName || DEFAULT_TOKEN_SYMBOL;
  const ethPrice = safeFromWei(item.ETHPrice, "ether");
  const expTimestamp = Number(item.expDate || 0);
  const isExpired = expTimestamp > 0 && expTimestamp <= moment().unix();
  const expFormatted = expTimestamp > 0 ? moment.unix(expTimestamp).format("DD MMM YYYY, HH:mm") : "No Expiry";
  const expRelative = expTimestamp > 0 ? moment.unix(expTimestamp).fromNow() : "";
  const imageUrl = getImageUrlByHash(item.logoHash, giftcardLogo) || AutobetLogo;

  const handleCopyCreator = (e) => {
    e.stopPropagation();
    if (!item.creator) return;
    try {
      navigator.clipboard.writeText(item.creator);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy error:", err);
    }
  };

  const handleCardClick = () => {
    history.push(`/giftcard/${cardId}`);
  };

  const handleShareClick = (e) => {
    e.stopPropagation();
    if (onShare) {
      onShare(item);
    }
  };

  // ----------------------------------------------------
  // LIST VIEW LAYOUT
  // ----------------------------------------------------
  if (viewMode === "list") {
    return (
      <Card
        className="public-giftcard-card"
        onClick={handleCardClick}
        sx={{
          borderRadius: borderRadiusPx.xl,
          backgroundColor: colors.dashboard.cardBg,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          boxShadow: customShadows.cardFlat,
          p: 2.5,
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
          "&:hover": {
            borderColor: colors.blue[300],
            boxShadow: customShadows.cardHover,
          },
        }}
      >
        <Grid container spacing={2.5} alignItems="center">
          {/* Logo & ID */}
          <Grid item xs={12} sm={3} md={2.5}>
            <Box
              sx={{
                width: "100%",
                height: 120,
                borderRadius: borderRadiusPx.lg,
                bgcolor: colors.slate[50],
                border: `1px solid ${colors.slate[200]}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 1.5,
                position: "relative",
              }}
            >
              <Box
                component="img"
                src={imageUrl}
                alt="Gift Card Logo"
                sx={{
                  maxHeight: "100%",
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = AutobetLogo;
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 6,
                  left: 6,
                  bgcolor: "rgba(15, 23, 42, 0.75)",
                  color: colors.neutral.white,
                  px: 0.75,
                  py: 0.2,
                  borderRadius: borderRadiusPx.sm,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                }}
              >
                #{cardId}
              </Box>
            </Box>
          </Grid>

          {/* Details & Info */}
          <Grid item xs={12} sm={6} md={6.5}>
            <Stack spacing={1}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {isDonation ? (
                  <CommonChip
                    label="Free Voucher"
                    startIcon={<DonationIcon size={12} />}
                    size="small"
                    sx={{
                      bgcolor: colors.amber[50],
                      color: colors.amber[800],
                      fontWeight: 700,
                      border: `1px solid ${colors.amber[200]}`,
                    }}
                  />
                ) : (
                  <CommonChip
                    label="Fixed Price"
                    startIcon={<SaleIcon size={12} />}
                    size="small"
                    sx={{
                      bgcolor: colors.blue[50],
                      color: colors.primary.main,
                      fontWeight: 700,
                      border: `1px solid ${colors.blue[200]}`,
                    }}
                  />
                )}

                <Typography
                  variant="caption"
                  sx={{
                    color: isExpired ? colors.rose[600] : colors.slate[500],
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <CalendarIcon size={12} />
                  {isExpired ? "Expired" : `Valid: ${expFormatted} (${expRelative})`}
                </Typography>
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 900,
                  color: colors.slate[900],
                  fontSize: "1.35rem",
                  textAlign: "left",
                }}
              >
                {tokenValue} {tokenName}
              </Typography>

              {item.Desc && (
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.slate[600],
                    fontSize: "0.85rem",
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textAlign: "left",
                  }}
                >
                  {item.Desc}
                </Typography>
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="caption" sx={{ color: colors.slate[500], fontWeight: 600 }}>
                  Creator:
                </Typography>
                <Typography variant="caption" sx={{ color: colors.slate[800], fontWeight: 700, fontFamily: "monospace" }}>
                  {shortenAddress(item.creator)}
                </Typography>
                <Tooltip title={copied ? "Copied!" : "Copy Creator Address"}>
                  <IconButton size="small" onClick={handleCopyCreator} sx={{ p: 0.3 }}>
                    {copied ? <CheckIcon fontSize="inherit" color="success" /> : <CopyIcon size={12} color={colors.slate[400]} />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Stack>
          </Grid>

          {/* Price & Action Button */}
          <Grid item xs={12} sm={3} md={3}>
            <Stack spacing={1.5} alignItems={{ xs: "flex-start", sm: "flex-end" }}>
              <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                <Typography variant="caption" sx={{ color: colors.slate[500], fontWeight: 600, textTransform: "uppercase" }}>
                  Cost
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    color: isDonation ? colors.emerald[600] : colors.slate[900],
                  }}
                >
                  {isDonation ? "FREE + Gas" : `${ethPrice} ${DEFAULT_CURRENCY}`}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} sx={{ width: "100%", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
                <CommonButton
                  variant="primary"
                  size="small"
                  onClick={handleCardClick}
                  endIcon={<ArrowRight size={14} />}
                >
                  {isDonation ? "Redeem Free" : "Buy Card"}
                </CommonButton>

                {onShare && (
                  <Tooltip title="Share Gift Card">
                    <IconButton
                      size="small"
                      onClick={handleShareClick}
                      sx={{
                        bgcolor: colors.slate[50],
                        border: `1px solid ${colors.slate[200]}`,
                        color: colors.slate[600],
                        "&:hover": { bgcolor: colors.blue[50], color: colors.primary.main },
                      }}
                    >
                      <Share2 size={14} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Card>
    );
  }

  // ----------------------------------------------------
  // GRID VIEW LAYOUT (DEFAULT)
  // ----------------------------------------------------
  return (
    <Card
      className="public-giftcard-card"
      onClick={handleCardClick}
      sx={{
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        cursor: "pointer",
        position: "relative",
        transition: "transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s ease, border-color 0.25s ease",
        "&:hover": {
          borderColor: colors.blue[300],
          boxShadow: customShadows.cardHover,
        },
      }}
    >
      {/* Top Media Header */}
      <Box
        sx={{
          height: 160,
          bgcolor: colors.slate[50],
          borderBottom: `1px solid ${colors.dashboard.tableBorder}`,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2.5,
          background: isDonation
            ? `radial-gradient(circle at top right, ${colors.amber[50]}, ${colors.slate[50]})`
            : `radial-gradient(circle at top right, ${colors.blue[50]}, ${colors.slate[50]})`,
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt="Gift Card Logo"
          sx={{
            maxHeight: "100%",
            maxWidth: "80%",
            objectFit: "contain",
            transition: "transform 0.3s ease",
            ".public-giftcard-card:hover &": {
              transform: "scale(1.05)",
            },
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = AutobetLogo;
          }}
        />

        {/* Card ID Badge */}
        <Box
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            bgcolor: "rgba(15, 23, 42, 0.8)",
            color: colors.neutral.white,
            px: 1,
            py: 0.35,
            borderRadius: borderRadiusPx.sm,
            fontSize: "0.72rem",
            fontWeight: 800,
            backdropFilter: "blur(4px)",
          }}
        >
          #{cardId}
        </Box>

        {/* Category Chip Badge */}
        <Box sx={{ position: "absolute", top: 12, right: 12 }}>
          {isDonation ? (
            <CommonChip
              label="Free Voucher"
              startIcon={<DonationIcon size={12} />}
              size="small"
              sx={{
                bgcolor: colors.amber[50],
                color: colors.amber[800],
                fontWeight: 700,
                border: `1px solid ${colors.amber[200]}`,
              }}
            />
          ) : (
            <CommonChip
              label="Fixed Price"
              startIcon={<SaleIcon size={12} />}
              size="small"
              sx={{
                bgcolor: colors.blue[50],
                color: colors.primary.main,
                fontWeight: 700,
                border: `1px solid ${colors.blue[200]}`,
              }}
            />
          )}
        </Box>
      </Box>

      {/* Card Content Body */}
      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", flexGrow: 1 }}>
        {/* Token Value Display */}
        <Box sx={{ mb: 1.5, textAlign: "left" }}>
          <Typography
            variant="caption"
            sx={{
              color: colors.slate[500],
              fontWeight: 700,
              textTransform: "uppercase",
              fontSize: "0.72rem",
            }}
          >
            Gift Value
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              color: colors.slate[900],
              fontSize: "1.65rem",
              lineHeight: 1.2,
            }}
          >
            {tokenValue} <span style={{ fontSize: "1.1rem", color: colors.primary.main }}>{tokenName}</span>
          </Typography>
        </Box>

        {/* Price Box */}
        <Box
          sx={{
            p: 1.5,
            borderRadius: borderRadiusPx.md,
            bgcolor: isDonation ? colors.amber[50] : colors.slate[50],
            border: `1px solid ${isDonation ? colors.amber[200] : colors.slate[200]}`,
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="caption" sx={{ color: colors.slate[600], fontWeight: 700 }}>
            Cost:
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 800,
              color: isDonation ? colors.emerald[700] : colors.slate[900],
            }}
          >
            {isDonation ? "FREE (+ Gas)" : `${ethPrice} ${DEFAULT_CURRENCY}`}
          </Typography>
        </Box>

        {/* Expiry and Creator Info */}
        <Stack spacing={1} sx={{ mb: 2.5, textAlign: "left" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: colors.slate[500] }}>
            <CalendarIcon size={14} color={colors.slate[400]} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: isExpired ? colors.rose[600] : colors.slate[600] }}>
              {isExpired ? "Expired" : `Expires ${expFormatted}`}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="caption" sx={{ color: colors.slate[500], fontWeight: 600 }}>
              Creator:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 700,
                  color: colors.slate[800],
                }}
              >
                {shortenAddress(item.creator)}
              </Typography>
              <Tooltip title={copied ? "Copied!" : "Copy Creator"}>
                <IconButton size="small" onClick={handleCopyCreator} sx={{ p: 0.2 }}>
                  {copied ? <CheckIcon fontSize="inherit" color="success" /> : <CopyIcon size={12} color={colors.slate[400]} />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {item.Desc && (
            <Typography
              variant="body2"
              sx={{
                color: colors.slate[600],
                fontSize: "0.82rem",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.4,
                pt: 0.5,
              }}
            >
              {item.Desc}
            </Typography>
          )}
        </Stack>

        {/* Bottom Actions Row */}
        <Box sx={{ mt: "auto", pt: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <CommonButton
            variant="primary"
            fullWidth
            onClick={handleCardClick}
            endIcon={<ArrowRight size={16} />}
          >
            {isDonation ? "Redeem Free" : "Buy Card"}
          </CommonButton>

          {onShare && (
            <Tooltip title="Share Card Link">
              <IconButton
                size="small"
                onClick={handleShareClick}
                sx={{
                  bgcolor: colors.slate[50],
                  border: `1px solid ${colors.slate[200]}`,
                  color: colors.slate[600],
                  p: 1,
                  "&:hover": {
                    bgcolor: colors.blue[50],
                    color: colors.primary.main,
                    borderColor: colors.blue[200],
                  },
                }}
              >
                <Share2 size={16} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Card>
  );
};

PublicGiftCard.propTypes = {
  item: PropTypes.object.isRequired,
  giftcardLogo: PropTypes.array,
  viewMode: PropTypes.oneOf(["grid", "list"]),
  onShare: PropTypes.func,
};

export default PublicGiftCard;
